import { useMutation } from '@apollo/client/react'
import { useCallback } from 'react'

import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

export const DismissOrganizationObject = graphql(/* GraphQL */ `
  mutation DismissOrganizationObject(
    $organizationId: String!
    $dismissedObject: DismissedObjectInput!
  ) {
    addOrUpdateDismissedObject(
      organizationId: $organizationId
      dismissedObject: $dismissedObject
    ) {
      __typename
      ... on AddOrUpdateDismissedObjectResponse {
        success
      }
      ... on AddOrUpdateDismissedObjectError {
        cause
        userFriendlyMessage
      }
    }
  }
`)

type DismissObjectInput = {
  id: string
  /**
   * ISO timestamp after which the dismissal lapses. The backend filters
   * expired entries out of `dismissedObjects` on read, so no client-side
   * timer logic is needed. Omit for a permanent dismissal.
   */
  expiresAt?: string
}

type UseDismissObjectReturn = {
  dismissObject: (input: DismissObjectInput) => void
}

/**
 * Persists an org-level dismissed object and mirrors it into the Apollo
 * cache so `hasDismissedObject` reads reflect the dismissal immediately.
 */
export const useDismissObject = (): UseDismissObjectReturn => {
  const { account } = useAccount()
  const [mutate] = useMutation(DismissOrganizationObject)
  const organization = account?.currentOrganization
  const organizationId = organization?.id

  const dismissObject = useCallback(
    ({ id, expiresAt }: DismissObjectInput): void => {
      if (!organization || !organizationId) {
        return
      }

      void mutate({
        variables: {
          organizationId,
          dismissedObject: { id, ...(expiresAt ? { expiresAt } : {}) },
        },
        update: (cache, { data }) => {
          const result = data?.addOrUpdateDismissedObject
          // Only hide the surface when the dismissal actually persisted —
          // otherwise it would reappear on the next load anyway.
          if (
            result?.__typename !== 'AddOrUpdateDismissedObjectResponse' ||
            !result.success
          ) {
            return
          }

          cache.modify({
            id: cache.identify(organization),
            fields: {
              dismissedObjects(existingObjects = []) {
                if (
                  existingObjects.some(
                    (entry: { id?: string }) => entry?.id === id,
                  )
                ) {
                  return existingObjects
                }
                return [
                  ...existingObjects,
                  { __typename: 'DismissedObject', id },
                ]
              },
            },
          })
        },
      })
    },
    [mutate, organization, organizationId],
  )

  return { dismissObject }
}
